// src/pages/qc/PipelineDetailModal.jsx
import { useState, useEffect } from "react";
import { fmtDate } from "../../utils/dateUtils";
import { STAGE_STYLE, STATUS_STYLE, PRIORITY_STYLE, STAGES, STATUSES } from "./PageRdPipeline";
import { PipelineCreateModal } from "./PipelineCreateModal";

function parseStageNotes(json) {
  if (!json) return {};
  try { return JSON.parse(json); } catch { return {}; }
}

function Badge({ map, value }) {
  const s = map[value] ?? { bg: "#f3f4f6", color: "#374151", label: value };
  return (
    <span style={{ background: s.bg, color: s.color, borderRadius: 4,
      padding: "2px 10px", fontSize: 12, fontWeight: 600 }}>
      {s.label}
    </span>
  );
}

function DetailRow({ label, value }) {
  return (
    <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
      <span style={{ color: "#6b7280", fontSize: 12, minWidth: 160 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 500, color: value ? "#111" : "#bbb" }}>
        {value || "—"}
      </span>
    </div>
  );
}

const STAGE_ORDER = ["CONCEPT","LAB_TRIAL","SCALE_UP","EVALUATION","APPROVED","LAUNCHED"];

function StageProgress({ stage }) {
  const idx = STAGE_ORDER.indexOf(stage);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 0, margin: "12px 0 16px" }}>
      {STAGE_ORDER.map((s, i) => {
        const style   = STAGE_STYLE[s];
        const done    = i < idx;
        const current = i === idx;
        return (
          <div key={s} style={{ flex: 1, display: "flex", alignItems: "center" }}>
            <div style={{
              flex: 1, height: 6, background: (done || current) ? style.color : "#e5e7eb",
              borderRadius: i === 0 ? "4px 0 0 4px" : i === STAGE_ORDER.length - 1 ? "0 4px 4px 0" : 0,
            }} />
            {i < STAGE_ORDER.length - 1 && (
              <div style={{ width: 0, height: 0,
                borderTop: "6px solid transparent",
                borderBottom: "6px solid transparent",
                borderLeft: `8px solid ${(done || current) ? style.color : "#e5e7eb"}`,
              }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export function PipelineDetailModal({ project, canEdit, canDelete, onUpdate, onDelete, onClose }) {
  const [p,          setP]          = useState(project);
  const [showEdit,   setShowEdit]   = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [stageNote,  setStageNote]  = useState(() => {
    const notes = parseStageNotes(project.stageNotes);
    return notes[project.stage] ?? "";
  });

  // Sync p with parent when parent state updates (e.g., after advanceStage)
  useEffect(() => {
    setP(project);
  }, [project]);

  // When stage changes, load the saved note for the new stage
  useEffect(() => {
    const notes = parseStageNotes(p.stageNotes);
    setStageNote(notes[p.stage] ?? "");
  }, [p.stage, p.stageNotes]);

  function buildBody(overrides = {}) {
    return {
      name: p.name, productType: p.productType, description: p.description,
      assignedTo: p.assignedTo, priority: p.priority, stage: p.stage,
      status: p.status, startDate: p.startDate, targetDate: p.targetDate,
      notes: p.notes, stageNotes: p.stageNotes,
      ...overrides,
    };
  }

  async function handleEditSave(body) {
    const updated = await onUpdate(p.id, body);
    if (updated) setP(updated);
    setShowEdit(false);
  }

  async function saveCurrentNote() {
    setSavingNote(true);
    try {
      const notes = parseStageNotes(p.stageNotes);
      if (stageNote.trim()) notes[p.stage] = stageNote.trim();
      else delete notes[p.stage];
      const updated = await onUpdate(p.id, buildBody({ stageNotes: JSON.stringify(notes) }));
      if (updated) setP(updated);
    } finally {
      setSavingNote(false);
    }
  }

  async function advanceStage() {
    const idx = STAGE_ORDER.indexOf(p.stage);
    if (idx < 0 || idx >= STAGE_ORDER.length - 1) return;
    const next = STAGE_ORDER[idx + 1];
    setSaving(true);
    try {
      const notes = parseStageNotes(p.stageNotes);
      if (stageNote.trim()) notes[p.stage] = stageNote.trim();
      const updated = await onUpdate(p.id, buildBody({
        stage:      next,
        status:     next === "LAUNCHED" ? "COMPLETED" : p.status,
        stageNotes: JSON.stringify(notes),
      }));
      if (updated) setP(updated);
    } finally {
      setSaving(false);
    }
  }

  if (showEdit) {
    return (
      <PipelineCreateModal
        project={p}
        onSave={handleEditSave}
        onCancel={() => setShowEdit(false)}
      />
    );
  }

  const pri       = PRIORITY_STYLE[p.priority] ?? PRIORITY_STYLE.MEDIUM;
  const stageIdx  = STAGE_ORDER.indexOf(p.stage);
  const canAdvance = canEdit && stageIdx >= 0 && stageIdx < STAGE_ORDER.length - 1
    && p.status === "ACTIVE";
  const stageNotes = parseStageNotes(p.stageNotes);

  const sectionStyle = {
    fontWeight: 600, fontSize: 12, color: "#1a3a6b",
    borderBottom: "2px solid #1a3a6b", marginBottom: 12, paddingBottom: 4,
    display: "flex", justifyContent: "space-between", alignItems: "center",
  };

  return (
    <div className="modal" style={{ width: 680 }}>
      <div className="modal-header">
        <div>
          <div className="modal-title">{p.name}</div>
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 3, display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ fontFamily: "monospace", fontWeight: 600 }}>{p.projectCode}</span>
            <Badge map={STAGE_STYLE}  value={p.stage} />
            <Badge map={STATUS_STYLE} value={p.status} />
            <span style={{ fontWeight: 700, color: pri.color }}>{pri.label} Priority</span>
          </div>
        </div>
        <button className="modal-close" onClick={onClose}>✕</button>
      </div>

      <div className="modal-body" style={{ maxHeight: "75vh", overflowY: "auto" }}>

        {p.stage !== "LAUNCHED" && p.stage !== "CANCELLED" && (
          <>
            <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>Stage Progress</div>
            <StageProgress stage={p.stage} />
          </>
        )}

        {/* Project details */}
        <div style={sectionStyle}>
          <span>Project Details</span>
          {canEdit && (
            <button className="btn btn-sm btn-secondary" onClick={() => setShowEdit(true)}>Edit</button>
          )}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" }}>
          <DetailRow label="Product Type"   value={p.productType} />
          <DetailRow label="Assigned To"    value={p.assignedTo} />
          <DetailRow label="Start Date"     value={p.startDate  ? fmtDate(p.startDate)  : null} />
          <DetailRow label="Target Date"    value={p.targetDate ? fmtDate(p.targetDate) : null} />
          <DetailRow label="Created"        value={fmtDate(p.createdAt)} />
          <DetailRow label="Created By"     value={p.createdBy} />
        </div>

        {p.description && (
          <div style={{ margin: "12px 0", background: "#f9fafb", borderRadius: 6,
            padding: "10px 12px", fontSize: 13, color: "#374151",
            borderLeft: "3px solid #1a3a6b" }}>
            <div style={{ fontWeight: 600, fontSize: 11, color: "#6b7280", marginBottom: 4 }}>
              Target Application / Description
            </div>
            {p.description}
          </div>
        )}

        {p.notes && (
          <div style={{ marginTop: 8, background: "#f9fafb", borderRadius: 6,
            padding: "8px 12px", fontSize: 12, color: "#374151" }}>
            <span style={{ fontWeight: 600, color: "#6b7280", marginRight: 8 }}>Notes:</span>
            {p.notes}
          </div>
        )}

        {/* ── Stage Notes ── */}
        <div style={{ ...sectionStyle, marginTop: 18 }}>
          <span>Stage Notes</span>
        </div>

        {/* Past stage notes (read-only) */}
        {STAGE_ORDER.slice(0, stageIdx).map(s => {
          const note = stageNotes[s];
          const style = STAGE_STYLE[s];
          return (
            <div key={s} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: style.color,
                textTransform: "uppercase", marginBottom: 3 }}>
                {style.label}
              </div>
              {note ? (
                <div style={{ fontSize: 13, background: "#f9fafb", padding: "6px 10px",
                  borderRadius: 4, whiteSpace: "pre-wrap", borderLeft: `2px solid ${style.color}` }}>
                  {note}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: "#cbd5e1", fontStyle: "italic" }}>No notes added</div>
              )}
            </div>
          );
        })}

        {/* Current stage — editable */}
        {p.stage !== "CANCELLED" && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: STAGE_STYLE[p.stage]?.color ?? "#374151",
              textTransform: "uppercase", marginBottom: 4 }}>
              {STAGE_STYLE[p.stage]?.label ?? p.stage} — Current Stage
            </div>
            <textarea
              className="form-input"
              rows={3}
              disabled={!canEdit}
              value={stageNote}
              onChange={e => setStageNote(e.target.value)}
              placeholder={canEdit ? `Add notes for ${STAGE_STYLE[p.stage]?.label ?? p.stage} stage…` : "No notes"}
              style={{ marginBottom: 6 }}
            />
            {canEdit && (
              <button className="btn btn-sm" disabled={savingNote} onClick={saveCurrentNote}>
                {savingNote ? "Saving…" : "Save Notes"}
              </button>
            )}
          </div>
        )}

        {/* Advance stage */}
        {canAdvance && (
          <div style={{ marginTop: 16, padding: "12px 14px", background: "#f0f9ff",
            borderRadius: 8, border: "1px solid #bae6fd",
            display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontSize: 13, color: "#0369a1" }}>
              Advance to <strong>{STAGES.find(s => s.value === STAGE_ORDER[stageIdx + 1])?.label}</strong>
              <span style={{ fontSize: 11, color: "#64748b", marginLeft: 6 }}>
                (saves current notes)
              </span>
            </div>
            <button className="btn btn-primary btn-sm" disabled={saving}
              onClick={advanceStage}>
              {saving ? "Updating…" : "Advance Stage →"}
            </button>
          </div>
        )}

      </div>

      <div className="modal-footer">
        {canDelete && (
          <button className="btn btn-danger" onClick={() => onDelete(p.id)}>
            Archive Project
          </button>
        )}
        <button className="btn btn-secondary" style={{ marginLeft: "auto" }} onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}
